// Tour service for guided tour functionality
import Shepherd from 'shepherd.js';
import { Icon } from '@iconify/react';
import { ENV_CONFIG } from '../config/environment';

// CSS for Shepherd tour with DM Sans font and elevated design
const SHEPHERD_CSS = `
  @import url('${ENV_CONFIG.EXTERNAL.GOOGLE_FONTS_URL}');

  .shepherd-modal-overlay-container {
    z-index: 9999 !important;
  }
  
  .shepherd-element {
    z-index: 10000 !important;
    background: rgba(255, 255, 255, 0.98);
    backdrop-filter: blur(20px);
    border-radius: 20px;
    box-shadow: 
      0 32px 64px -12px rgba(0, 0, 0, 0.15),
      0 8px 16px -4px rgba(0, 0, 0, 0.08),
      0 0 0 1px rgba(255, 255, 255, 0.8),
      inset 0 1px 0 rgba(255, 255, 255, 0.9);
    border: 1px solid rgba(32, 101, 209, 0.12);
    max-width: 420px;
    min-width: 360px;
    font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
    position: relative;
    overflow: hidden;
  }
  
  .shepherd-element::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(32, 101, 209, 0.3), transparent);
  }
      .shepherd-header {
    background: linear-gradient(135deg, #2065D1 0%, #1976D2 100%);
    color: white;
    padding: 8px 8px 8px;
    border-radius: 16px 16px 0 0;
    font-weight: 600;
    font-size: 18px;
    margin: 0;
    position: relative;
    display: flex;
    align-items: center;
    gap: 8px;
  }
  
  .shepherd-header .iconify {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
  }
  
  .shepherd-header::after {
    content: '';
    position: absolute;
    bottom: -1px;
    left: 0;
    right: 0;
    height: 1px;
    background: rgba(255, 255, 255, 0.2);
  }
  
  .shepherd-text {
    padding: 20px 24px;
    font-size: 15px;
    line-height: 1.6;
    color: #374151;
    margin: 0;
  }
  
  .shepherd-text p {
    margin: 0 0 12px 0;
  }
  
  .shepherd-text p:last-child {
    margin-bottom: 0;
  }
  
  .shepherd-text ul {
    margin: 8px 0;
    padding-left: 20px;
  }
  
  .shepherd-text li {
    margin: 6px 0;
    color: #4B5563;
  }
  
  .shepherd-footer {
    padding: 8px 8px 8px;
    border-top: 1px solid #F3F4F6;
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-radius: 0 0 16px 16px;
    gap: 8px;
  }
  
  .shepherd-button {
    background: #2065D1;
    color: white;
    border: none;
    padding: 10px 20px;
    border-radius: 8px;
    cursor: pointer;
    font-size: 14px;
    font-weight: 500;
    transition: all 0.2s ease;
    min-width: 80px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
  
  .shepherd-button:hover {
    background: #1E40AF;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(32, 101, 209, 0.4);
  }
  
  .shepherd-button:active {
    transform: translateY(0);
  }
  
  .shepherd-button-secondary {
    background: white;
    color: #6B7280;
    border: 1px solid #D1D5DB;
  }
  
  .shepherd-button-secondary:hover {
    background: #F9FAFB;
    color: #374151;
    border-color: #9CA3AF;
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
  
  /* Arrow styling */
  .shepherd-arrow {
    border: 8px solid transparent;
    position: absolute;
  }
  
  .shepherd-arrow::before {
    border: 8px solid transparent;
    content: '';
    display: block;
    position: absolute;
  }
  
  /* Arrow positioning and colors */
  .shepherd-element[x-placement^="top"] .shepherd-arrow {
    bottom: -16px;
    border-top-color: white;
  }
  
  .shepherd-element[x-placement^="bottom"] .shepherd-arrow {
    top: -16px;
    border-bottom-color: white;
  }
  
  .shepherd-element[x-placement^="left"] .shepherd-arrow {
    right: -16px;
    border-left-color: white;
  }
  
  .shepherd-element[x-placement^="right"] .shepherd-arrow {
    left: -16px;
    border-right-color: white;
  }
  
  /* Modal overlay styling */
  .shepherd-modal-overlay-container .shepherd-modal-overlay {
    background-color: rgba(0, 0, 0, 0.5);
    backdrop-filter: blur(2px);
  }
    /* Highlighted element styling */
  .shepherd-target-highlight {
    border-radius: 12px !important;
    box-shadow: 0 0 0 4px rgba(32, 101, 209, 0.4) !important;
    transition: all 0.3s ease !important;
    overflow: visible !important;
  }
  
  /* Additional styling for highlighted elements */
  .shepherd-target-highlight * {
    border-radius: inherit !important;
  }
  
  /* Responsive design */
  @media (max-width: 768px) {
    .shepherd-element {
      max-width: 320px;
      margin: 20px;
    }
    
    .shepherd-header {
      padding: 16px 20px 12px;
      font-size: 16px;
    }
    
    .shepherd-text {
      padding: 16px 20px;
      font-size: 14px;
    }
    
    .shepherd-footer {
      padding: 12px 20px 16px;
      flex-direction: column;
      gap: 8px;
    }
    
    .shepherd-button {
      width: 100%;
      padding: 12px;
    }
  }
`;

class TourService {
  constructor() {
    this.tour = null;
    this.initializeCSS();
  }
  initializeCSS() {
    // Add CSS if not already present
    if (!document.getElementById('shepherd-tour-styles')) {
      const style = document.createElement('style');
      style.id = 'shepherd-tour-styles';
      style.textContent = SHEPHERD_CSS;
      document.head.appendChild(style);
    }
    
    // Add Iconify script if not already present
    if (!document.getElementById('iconify-script')) {
      const script = document.createElement('script');
      script.id = 'iconify-script';
      script.src = ENV_CONFIG.EXTERNAL.ICONIFY_SCRIPT_URL;
      document.head.appendChild(script);
    }
  }createTour() {
    if (this.tour) {
      this.tour.complete();
    }

    this.tour = new Shepherd.Tour({
      useModalOverlay: true,
      defaultStepOptions: {
        classes: 'shadow-md bg-white-card',
        scrollTo: { behavior: 'smooth', block: 'center' },
        modalOverlayOpeningPadding: 12,
        canClickTarget: false,
        popperOptions: {
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, 16]
              }
            },
            {
              name: 'preventOverflow',
              options: {
                boundary: 'viewport',
                padding: 24
              }
            },
            {
              name: 'flip',
              options: {
                fallbackPlacements: ['top', 'bottom', 'left', 'right']
              }
            }
          ]
        }
      }
    });

    return this.tour;
  }  startMainTour() {
    const tour = this.createTour();

    // Helper function to create title with Iconify icon
    const createTitle = (iconName, text) => {
      const iconElement = document.createElement('div');
      iconElement.style.display = 'flex';
      iconElement.style.alignItems = 'center';
      iconElement.style.gap = '8px';
      
      const icon = document.createElement('span');
      icon.innerHTML = `<iconify-icon icon="${iconName}" width="20" height="20"></iconify-icon>`;
      
      const textSpan = document.createElement('span');
      textSpan.textContent = text;
      
      iconElement.appendChild(icon);
      iconElement.appendChild(textSpan);
      
      return iconElement.outerHTML;
    };

    // Helper function to check if element exists and is visible
    const getValidElement = (selector) => {
      const element = document.querySelector(selector);
      if (element && element.offsetParent !== null) {
        return element;
      }
      return null;
    };    // Step 1: Welcome
    tour.addStep({
      title: createTitle('mdi:party-popper', 'Welcome to Parts360!'),
      text: `
        <div>
          <p>Welcome to the Parts360 AI Assistant! Let me show you around the main features of this powerful application.</p>
          <p>This tour will highlight the key areas and functionalities you can use to get the most out of Parts360.</p>
        </div>
      `,
      buttons: [
        {
          text: 'Skip Tour',
          classes: 'shepherd-button-secondary',
          action: tour.complete
        },
        {
          text: 'Start Tour',
          classes: 'shepherd-button',
          action: tour.next
        }
      ]
    });    // Step 2: Sidebar - Logo and branding
    tour.addStep({
      title: createTitle('mdi:menu', 'Sidebar Navigation'),
      text: `
        <div>
          <p>This is your main navigation sidebar. Here you can:</p>
          <ul>
            <li>• Access all your chat sessions</li>
            <li>• Create new conversations</li>
            <li>• Manage your account settings</li>
            <li>• Access the admin portal (if authorized)</li>
          </ul>
        </div>
      `,
      attachTo: {
        element: () => getValidElement('[data-tour="sidebar"]') || getValidElement('nav') || getValidElement('.MuiBox-root'),
        on: 'right'
      },
      buttons: [
        {
          text: 'Back',
          classes: 'shepherd-button-secondary',
          action: tour.back
        },
        {
          text: 'Next',
          classes: 'shepherd-button',
          action: tour.next
        }
      ]
    });    // Step 3: New Session Button
    tour.addStep({
      title: createTitle('mdi:plus-circle', 'Create New Sessions'),
      text: `
        <div>
          <p>Click this button to start a new conversation with the AI assistant.</p>
          <p>Each session maintains its own context, so you can have multiple conversations about different topics.</p>
        </div>
      `,
      attachTo: {
        element: () => getValidElement('[data-tour="new-session"]') || getValidElement('[aria-label*="New"]') || getValidElement('button'),
        on: 'bottom'
      },
      buttons: [
        {
          text: 'Back',
          classes: 'shepherd-button-secondary',
          action: tour.back
        },
        {
          text: 'Next',
          classes: 'shepherd-button',
          action: tour.next
        }
      ]
    });    // Step 4: Chat Sessions List
    tour.addStep({
      title: createTitle('mdi:chat', 'Your Conversations'),
      text: `
        <div>
          <p>All your chat sessions are organized here by date:</p>
          <ul>
            <li>• <strong>Today</strong> - Current day conversations</li>
            <li>• <strong>Yesterday</strong> - Previous day chats</li>
            <li>• <strong>Older</strong> - Historical conversations</li>
          </ul>
          <p>Click on any session to continue that conversation.</p>
        </div>
      `,
      attachTo: {
        element: () => getValidElement('[data-tour="session-list"]') || getValidElement('.MuiList-root') || getValidElement('ul'),
        on: 'right'
      },
      buttons: [
        {
          text: 'Back',
          classes: 'shepherd-button-secondary',
          action: tour.back
        },
        {
          text: 'Next',
          classes: 'shepherd-button',
          action: tour.next
        }
      ]
    });    // Step 5: Main Chat Area
    tour.addStep({
      title: createTitle('mdi:robot', 'AI Chat Interface'),
      text: `
        <div>
          <p>This is where the magic happens! The main chat area includes:</p>
          <ul>
            <li>• AI responses with actionable insights</li>
            <li>• Data tables and visualizations</li>
            <li>• Like/dislike feedback system</li>
            <li>• Copy functionality for responses</li>
          </ul>
        </div>
      `,
      attachTo: {
        element: () => getValidElement('[data-tour="chat-area"]') || getValidElement('main') || document.body,
        on: 'left'
      },
      buttons: [
        {
          text: 'Back',
          classes: 'shepherd-button-secondary',
          action: tour.back
        },
        {
          text: 'Next',
          classes: 'shepherd-button',
          action: tour.next
        }
      ]
    });    // Step 6: Message Input
    tour.addStep({
      title: createTitle('mdi:message-text', 'Send Messages'),
      text: `
        <div>
          <p>Type your questions here and press Enter or click the send button.</p>
          <p>You can ask about:</p>
          <ul>
            <li>• Part availability and inventory</li>
            <li>• Pricing and cost information</li>
            <li>• Historical quotes and proposals</li>
            <li>• Data analysis and reports</li>
          </ul>
        </div>
      `,
      attachTo: {
        element: () => getValidElement('[data-tour="message-input"]') || getValidElement('textarea') || getValidElement('input[type="text"]'),
        on: 'top'
      },
      buttons: [
        {
          text: 'Back',
          classes: 'shepherd-button-secondary',
          action: tour.back
        },
        {
          text: 'Next',
          classes: 'shepherd-button',
          action: tour.next
        }
      ]
    });

    // Step 7: AI Agent Selector (conditional)
    const agentSelectorElement = getValidElement('[data-tour="agent-selector"]') || getValidElement('select') || getValidElement('.MuiSelect-root');
    if (agentSelectorElement) {      tour.addStep({
        title: createTitle('mdi:target', 'AI Agent Selection'),
        text: `
          <div>
            <p>Choose between different AI agents for specialized tasks:</p>
            <ul>
              <li>• <strong>SQL Agent</strong> - Direct database queries</li>
              <li>• <strong>AI Search</strong> - Intelligent search capabilities</li>
            </ul>
            <p>Each agent is optimized for different types of questions.</p>
          </div>
        `,
        attachTo: {
          element: () => agentSelectorElement,
          on: 'top'
        },
        buttons: [
          {
            text: 'Back',
            classes: 'shepherd-button-secondary',
            action: tour.back
          },
          {
            text: 'Next',
            classes: 'shepherd-button',
            action: tour.next
          }
        ]
      });
    }    // Step 8: Authentication
    tour.addStep({
      title: createTitle('mdi:shield-account', 'Account Management'),
      text: `
        <div>
          <p>This area shows your authentication status and account controls:</p>
          <ul>
            <li>• Sign in with your Microsoft account</li>
            <li>• View your current session status</li>
            <li>• Access admin features (if authorized)</li>
          </ul>
        </div>
      `,
      attachTo: {
        element: () => getValidElement('[data-tour="auth-section"]') || getValidElement('button[aria-label*="Sign"]') || getValidElement('button:last-child'),
        on: 'top'
      },
      buttons: [
        {
          text: 'Back',
          classes: 'shepherd-button-secondary',
          action: tour.back
        },
        {
          text: 'Next',
          classes: 'shepherd-button',
          action: tour.next
        }
      ]    });

    // Step 9: Upload Parts Excel
    tour.addStep({
      title: createTitle('mdi:file-excel', 'Upload Parts Excel'),
      text: `
        <div>
          <p>Upload Excel files with parts data directly to the system:</p>
          <ul>
            <li>• <strong>Bulk Import</strong> - Upload large inventories quickly</li>
            <li>• <strong>Data Validation</strong> - Automatic format checking</li>
            <li>• <strong>Progress Tracking</strong> - Monitor upload status</li>
            <li>• <strong>Error Reports</strong> - Review any import issues</li>
          </ul>
          <p>Perfect for initial setup or bulk updates!</p>
        </div>
      `,
      attachTo: {
        element: () => getValidElement('[data-tour="upload-excel"]') || getValidElement('button[aria-label*="Upload"]') || getValidElement('input[type="file"]'),
        on: 'bottom'
      },
      buttons: [
        {
          text: 'Back',
          classes: 'shepherd-button-secondary',
          action: tour.back
        },
        {
          text: 'Next',
          classes: 'shepherd-button',
          action: tour.next
        }
      ]
    });    // Step 10: Tour & Tutorial Buttons
    tour.addStep({
      title: createTitle('mdi:school', 'Learning Resources'),
      text: `
        <div>
          <p>Use these helpful buttons anytime:</p>
          <ul>
            <li>• <strong>Quick Tour</strong> - Take this guided tour again</li>
            <li>• <strong>Tutorial</strong> - Access detailed feature documentation</li>
          </ul>
          <p>These are always available to help you navigate Parts360!</p>
        </div>
      `,
      attachTo: {
        element: () => getValidElement('[data-tour="tour-buttons"]') || getValidElement('button[aria-label*="Tour"]') || getValidElement('button:last-of-type'),
        on: 'top'
      },
      buttons: [
        {
          text: 'Back',
          classes: 'shepherd-button-secondary',
          action: tour.back
        },
        {
          text: 'Finish Tour',
          classes: 'shepherd-button',
          action: tour.complete
        }
      ]
    });

    tour.start();
    return tour;
  }

  completeTour() {
    if (this.tour) {
      this.tour.complete();
    }
  }
}

export const tourService = new TourService();
export default tourService;
